import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  PhoneCall, GitBranch, PhoneForwarded, Voicemail, Activity, Users,
  CreditCard, ArrowRight, Check, Zap, ShieldCheck, Rocket, WandSparkles, PhoneOutgoing, Play,
} from "lucide-react";

const C = {
  emerald: "#10b981", violet: "#8b5cf6", amber: "#f59e0b",
  slate: "#64748b", indigo: "#6366f1", rose: "#f43f5e", blue: "#3b82f6",
};

// id -> node geometry + content
const NODES = {
  start:    { x: 420, y: 24,  w: 160, h: 46, c: "emerald", t: "Start",              s: "Call initiated" },
  intro:    { x: 250, y: 120, w: 180, h: 54, c: "violet",  t: "Play Intro",         s: "AI voice greeting" },
  reprompt: { x: 570, y: 120, w: 180, h: 54, c: "violet",  t: "Play Re-prompt",     s: "“Are you still there?”" },
  wait:     { x: 250, y: 214, w: 180, h: 54, c: "amber",   t: "Wait for Customer",  s: "Voice activity / VAD" },
  silence:  { x: 570, y: 214, w: 180, h: 54, c: "slate",   t: "Silence",            s: "Max 2 attempts" },
  response: { x: 330, y: 314, w: 340, h: 60, c: "indigo",  t: "Customer Response",  s: "AI intent + sentiment router" },
  noans:    { x: 740, y: 314, w: 170, h: 54, c: "rose",    t: "Hang Up",            s: "No answer" },
  great:    { x: 140, y: 436, w: 190, h: 54, c: "violet",  t: "Play: Great!",       s: "“Connecting you now.”" },
  thanks:   { x: 405, y: 436, w: 190, h: 54, c: "violet",  t: "Play: Thanks, bye",  s: "Not interested" },
  callback: { x: 670, y: 436, w: 190, h: 54, c: "violet",  t: "Play: Callback",     s: "“We’ll call you back.”" },
  transfer: { x: 140, y: 534, w: 190, h: 54, c: "blue",    t: "Transfer to Agent",  s: "SIP warm hand-off" },
  hangup1:  { x: 405, y: 534, w: 190, h: 54, c: "rose",    t: "Hang Up",            s: "not_interested" },
  hangup2:  { x: 670, y: 534, w: 190, h: 54, c: "rose",    t: "Hang Up",            s: "callback" },
  end:      { x: 430, y: 616, w: 160, h: 46, c: "slate",   t: "End",                s: "Flow complete" },
};

const EDGES = [
  ["start", "intro"], ["start", "reprompt", "retry"],
  ["intro", "wait"], ["reprompt", "wait"],
  ["wait", "response", "speech"], ["wait", "silence", "silence"],
  ["silence", "noans", "max"],
  ["response", "great", "interested"], ["response", "thanks", "not interested"], ["response", "callback", "callback"],
  ["great", "transfer"], ["thanks", "hangup1"], ["callback", "hangup2"],
  ["transfer", "end"], ["hangup1", "end"], ["hangup2", "end"], ["noans", "end"],
];

function edgePath(a, b) {
  const sx = a.x + a.w / 2, sy = a.y + a.h;
  const tx = b.x + b.w / 2, ty = b.y;
  const dy = Math.max(30, (ty - sy) / 2);
  return `M ${sx} ${sy} C ${sx} ${sy + dy}, ${tx} ${ty - dy}, ${tx} ${ty}`;
}

function FlowDiagram() {
  return (
    <div className="rounded-2xl border border-white/10 shadow-2xl shadow-violet-950/40 overflow-hidden"
      style={{ background: "#0c0d12" }} data-testid="call-flow-diagram">
      <div className="flex items-center gap-2 px-4 h-11 border-b border-white/10" style={{ background: "#121217" }}>
        <span className="h-3 w-3 rounded-full" style={{ background: "#f43f5e" }} />
        <span className="h-3 w-3 rounded-full" style={{ background: "#f59e0b" }} />
        <span className="h-3 w-3 rounded-full" style={{ background: "#10b981" }} />
        <span className="ml-3 text-xs font-mono tracking-widest" style={{ color: "#64748b" }}>
          campaign-designer — real-estate-qualification.flow
        </span>
        <span className="ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full"
          style={{ color: C.violet, background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.3)" }}>
          14 nodes · 17 paths
        </span>
      </div>
      <svg viewBox="0 0 1000 690" className="w-full h-auto block" role="img" aria-label="AI call flow diagram">
        <defs>
          <pattern id="dots" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#27272a" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="1000" height="690" fill="url(#dots)" />
        {EDGES.map(([from, to, label], i) => {
          const a = NODES[from], b = NODES[to];
          const d = edgePath(a, b);
          const mx = (a.x + a.w / 2 + b.x + b.w / 2) / 2;
          const my = (a.y + a.h + b.y) / 2;
          return (
            <g key={i}>
              <path d={d} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="2" />
              <path d={d} className="flow-edge" fill="none" stroke={C[b.c]} strokeWidth="2"
                strokeOpacity="0.6" style={{ animationDelay: `${(i % 8) * 0.15}s` }} />
              {label && (
                <text x={mx} y={my} textAnchor="middle" fontSize="10" fontFamily="monospace"
                  fill="#64748b" style={{ paintOrder: "stroke", stroke: "#0c0d12", strokeWidth: 4 }}>
                  {label}
                </text>
              )}
            </g>
          );
        })}
        {Object.entries(NODES).map(([id, n], i) => (
          <g key={id} className="flow-node" style={{ animationDelay: `${i * 0.06}s` }}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="12"
              fill={C[n.c]} fillOpacity="0.12" stroke={C[n.c]} strokeOpacity="0.55" strokeWidth="1.5" />
            <rect x={n.x} y={n.y + 10} width="4" height={n.h - 20} rx="2" fill={C[n.c]} />
            <text x={n.x + 16} y={n.y + 24} fontSize="14" fontWeight="700" fill="#f8fafc"
              fontFamily="Outfit, sans-serif">{n.t}</text>
            <text x={n.x + 16} y={n.y + 41} fontSize="11" fill="#94a3b8"
              fontFamily="'DM Sans', sans-serif">{n.s}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

const FEATURES = [
  { icon: WandSparkles, badge: "Core Studio", title: "Visual Drag-and-Drop Flow Designer",
    body: "Build multi-branch call journeys on an infinite canvas — play audio, wait for speech, capture DTMF, branch on intent, and hand off to humans. No code." },
  { icon: PhoneForwarded, badge: "Telephony", title: "Warm Human Transfer",
    body: "The moment a prospect is interested, the bot bridges the live SIP call to your agent with zero audio drops — context intact." },
  { icon: Voicemail, badge: "AI Guardrail", title: "Answering-Machine Detection",
    body: "Detects human vs voicemail in ~1 second and routes accordingly, so agents only talk to real people." },
  { icon: Activity, badge: "Control Room", title: "Live Calls & Real-Time CDRs",
    body: "Watch every active call, dispositions, and outcomes update live — with full call-detail records and transcripts." },
  { icon: Zap, badge: "Scale", title: "Concurrent-Call Scaling",
    body: "Run thousands of simultaneous conversations across a fleet of AI engines with race-safe concurrency limits per account." },
  { icon: Users, badge: "Enterprise", title: "Multi-Tenant: Admin · Reseller · Customer",
    body: "Isolated accounts, reseller sub-billing, per-channel pricing, and role-based control across the whole platform." },
];

const STEPS = [
  { n: "01", t: "Design the flow", d: "Drag nodes to script your greeting, questions, branches and hand-offs in the visual designer." },
  { n: "02", t: "Connect telephony", d: "Point your SIP trunks / VICIdial and AI engines. Set your concurrent-channel capacity." },
  { n: "03", t: "Launch & scale", d: "Dispatch the campaign. The AI qualifies leads and transfers hot prospects to agents in real time." },
];

const FAQ = [
  { q: "Do I need to write any code?", a: "No. Everything — greetings, branching, DTMF, silence handling and agent transfer — is built visually in the Campaign Designer." },
  { q: "How does the human hand-off work?", a: "When a node hits “Transfer to Agent”, WA Calls bridges the live call to your agent over SIP/PSTN while preserving the conversation context." },
  { q: "Can it detect voicemail?", a: "Yes. Built-in Answering-Machine Detection classifies human vs machine within about a second and routes the call accordingly." },
  { q: "How is capacity billed?", a: "You buy concurrent-call channels from your cash balance (prorated), with monthly renewal. Admins and resellers manage pricing per account." },
];

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-sm text-slate-300">
    {children}
  </span>
);

export default function Landing() {
  const [introVideos, setIntroVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  // Inject the admin-configured chat widget snippet (e.g. tawk.to) — landing page ONLY.
  useEffect(() => {
    let injected = [];
    let cancelled = false;
    api.get("/settings/public").then(({ data }) => {
      if (cancelled) return;
      const snippet = (data?.chat_widget_script || "").trim();
      if (!snippet) return;
      const tmpl = document.createElement("template");
      tmpl.innerHTML = snippet;
      Array.from(tmpl.content.childNodes).forEach((node) => {
        if (node.nodeName === "SCRIPT") {
          const s = document.createElement("script");
          for (const attr of node.attributes) s.setAttribute(attr.name, attr.value);
          s.text = node.textContent || "";
          document.body.appendChild(s);
          injected.push(s);
        } else if (node.nodeType === 1) {
          document.body.appendChild(node);
          injected.push(node);
        }
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
      injected.forEach((n) => n.remove());
      // Best-effort: hide/remove the common tawk.to widget when leaving the landing page.
      try { window.Tawk_API?.hideWidget?.(); } catch { /* noop */ }
      document.querySelectorAll('iframe[src*="tawk.to"], #tawkchat-container, .widget-visible')
        .forEach((el) => el.remove());
    };
  }, []);

  // Admin-uploaded landing intro videos (covers + playable video).
  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setIntroVideos(data?.intro_videos || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen text-slate-100 antialiased" style={{ background: "#09090b", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        .disp{font-family:'Outfit',sans-serif}
        @keyframes dash{to{stroke-dashoffset:-24}}
        .flow-edge{stroke-dasharray:6 6;animation:dash 1.1s linear infinite}
        @keyframes nodein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .flow-node{animation:nodein .5s ease both;transform-box:fill-box;transform-origin:center}
        @keyframes rise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        .rise{animation:rise .6s ease both}
        .glow{box-shadow:0 0 0 1px rgba(139,92,246,.25),0 30px 80px -20px rgba(139,92,246,.35)}
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/10" style={{ background: "rgba(9,9,11,0.75)" }}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: C.violet }}>
              <PhoneCall className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <p className="disp font-bold tracking-tight">WA Calls</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">AI Voice Agents</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 ml-8 text-sm text-slate-400">
            <a href="#flow" className="hover:text-white transition-colors">Call Flow</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/login" data-testid="nav-signin"
              className="text-sm text-slate-300 hover:text-white transition-colors px-3 py-2">Sign in</Link>
            <Link to="/signup" data-testid="nav-get-started"
              className="text-sm font-semibold text-white rounded-lg px-4 py-2 transition-colors"
              style={{ background: C.violet }}>Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.18), transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-14 relative">
          <div className="max-w-3xl rise">
            <span className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: C.violet }}>
              AI Telephony Control Plane
            </span>
            <h1 className="disp mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              AI voice agents that call, qualify,<br className="hidden sm:block" /> and transfer to your team.
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-2xl">
              WA Calls runs human-like outbound &amp; inbound phone conversations at scale. Design the
              call flow visually, let the bot handle the talking, and warm-transfer hot leads straight
              to a live agent — all on your own SIP infrastructure.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="hero-get-started"
                className="inline-flex items-center gap-2 text-white font-semibold rounded-xl px-6 py-3.5 transition-transform hover:-translate-y-0.5"
                style={{ background: C.violet }}>
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#flow"
                className="inline-flex items-center gap-2 text-slate-200 font-medium rounded-xl px-6 py-3.5 border border-white/15 hover:bg-white/5 transition-colors">
                See a live call flow
              </a>
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Pill><Zap className="w-4 h-4" style={{ color: C.amber }} /> Sub-second responses</Pill>
              <Pill><ShieldCheck className="w-4 h-4" style={{ color: C.emerald }} /> AMD &amp; human transfer</Pill>
              <Pill><PhoneOutgoing className="w-4 h-4" style={{ color: C.blue }} /> Thousands of concurrent calls</Pill>
            </div>
          </div>
        </div>
      </section>

      {/* Call flow diagram */}
      <section id="flow" className="max-w-7xl mx-auto px-6 py-16">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: C.violet }}>Visual Campaign Designer</span>
          <h2 className="disp mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Every call follows a flow you design</h2>
          <p className="mt-4 text-slate-400">
            This is a real WA Calls campaign: greet the prospect, listen, understand intent, then branch —
            connect the interested ones to an agent, thank the rest, and log everything.
          </p>
        </div>
        <div className="mt-10 glow rounded-2xl">
          <FlowDiagram />
        </div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
          {[["Start", C.emerald], ["Play audio", C.violet], ["Listen", C.amber],
            ["Intent branch", C.indigo], ["Transfer", C.blue], ["Hang up", C.rose]].map(([l, c]) => (
            <span key={l} className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} /> {l}
            </span>
          ))}
        </div>
      </section>

      {/* Intro videos */}
      {introVideos.length > 0 && (
        <section id="intro-videos" className="max-w-7xl mx-auto px-6 py-16" data-testid="landing-intro-videos">
          <div className="max-w-2xl">
            <span className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: C.violet }}>Watch &amp; learn</span>
            <h2 className="disp mt-3 text-3xl sm:text-4xl font-bold tracking-tight">See WA Calls in action</h2>
            <p className="mt-4 text-slate-400">Quick walkthroughs to get you configured and integrated fast.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {introVideos.map((v) => (
              <button
                key={v.id}
                data-testid={`intro-video-card-${v.id}`}
                onClick={() => setActiveVideo(v)}
                className="group relative text-left rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-white/20 transition-all duration-300"
              >
                <div className="aspect-video bg-black/40 relative">
                  {v.has_cover ? (
                    <img
                      src={`${API}/settings/intro-videos/${v.id}/cover`}
                      alt={v.title}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-600"><Play className="w-10 h-10" /></div>
                  )}
                  <span className="absolute inset-0 grid place-items-center">
                    <span
                      className="w-16 h-16 rounded-full grid place-items-center backdrop-blur-sm transition-transform duration-300 group-hover:scale-110"
                      style={{ background: "rgba(139,92,246,0.85)", boxShadow: "0 8px 30px rgba(139,92,246,0.5)" }}
                    >
                      <Play className="w-7 h-7 text-white translate-x-0.5" fill="white" />
                    </span>
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="disp text-lg font-semibold">{v.title}</h3>
                  <p className="mt-1 text-sm text-slate-400 inline-flex items-center gap-1">Play video <ArrowRight className="w-4 h-4" /></p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      <Dialog open={!!activeVideo} onOpenChange={(o) => !o && setActiveVideo(null)}>
        <DialogContent className="max-w-3xl bg-[#0b0b0f] border-white/10 p-0 overflow-hidden" data-testid="intro-video-modal">
          <DialogHeader className="px-5 pt-4">
            <DialogTitle className="text-slate-100">{activeVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="p-4 pt-2">
            {activeVideo && (
              <video
                key={activeVideo.id}
                src={`${API}/settings/intro-videos/${activeVideo.id}/video`}
                controls
                autoPlay
                playsInline
                className="w-full rounded-lg bg-black"
                data-testid="intro-video-player"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: C.violet }}>Platform</span>
          <h2 className="disp mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Everything you need to run voice at scale</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-300"
              style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
              <div className="w-11 h-11 rounded-xl grid place-items-center mb-4"
                style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.25)" }}>
                <f.icon className="w-5 h-5" style={{ color: C.violet }} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.badge}</p>
              <h3 className="disp mt-1.5 text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-16">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: C.violet }}>How it works</span>
          <h2 className="disp mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Live in three steps</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
              <p className="disp text-5xl font-extrabold" style={{ color: "rgba(139,92,246,0.35)" }}>{s.n}</p>
              <h3 className="disp mt-3 text-xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="disp text-3xl sm:text-4xl font-bold tracking-tight text-center">Questions, answered</h2>
        <div className="mt-10 space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} data-testid={`faq-${i}`} className="group rounded-xl border border-white/10 bg-white/[0.03] p-5 open:bg-white/[0.05]">
              <summary className="flex items-center justify-between cursor-pointer list-none font-medium">
                {f.q}
                <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-3xl border border-white/10 p-10 sm:p-16 text-center relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(9,9,11,0.2))" }}>
          <Rocket className="w-8 h-8 mx-auto mb-4" style={{ color: C.violet }} />
          <h2 className="disp text-3xl sm:text-4xl font-bold tracking-tight">Ready to launch your first AI voice campaign?</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">Sign in to the control plane and put a whole team of voice agents on the phones today.</p>
          <Link to="/signup" data-testid="cta-signin"
            className="inline-flex items-center gap-2 mt-8 text-white font-semibold rounded-xl px-7 py-3.5 transition-transform hover:-translate-y-0.5"
            style={{ background: C.violet }}>
            Create your account <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-4 text-xs text-slate-500 flex items-center justify-center gap-2">
            <Check className="w-3.5 h-3.5" style={{ color: C.emerald }} /> Admin, reseller &amp; customer access
          </p>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center gap-3 justify-between text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md grid place-items-center" style={{ background: C.violet }}>
              <PhoneCall className="w-3.5 h-3.5 text-white" />
            </div>
            WA Calls — AI Voice Agent &amp; Telephony Platform
          </div>
          <p>© {new Date().getFullYear()} WA Calls. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
